import { useEffect, useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Legend,
} from 'recharts';
import { ChevronDown, ChevronRight } from 'lucide-react';
import Modal from '../Modal';
import {
  tenantApi, PrimerQuestion, QuestionAnswerVersion, QuestionScoreEntry,
} from '../../lib/tenant-api';

interface QuestionPerformanceModalProps {
  question: PrimerQuestion | null;
  isOpen: boolean;
  onClose: () => void;
}

// Categorical palette (validated): mean line takes slot 1; platforms get fixed
// hues so a platform keeps its color no matter which reports it appears in.
const MEAN_COLOR = '#2a78d6';
const PLATFORM_COLOR_MAP: Record<string, string> = {
  chatgpt: '#eb6834', openai: '#eb6834',
  gemini: '#1baf7a', google: '#1baf7a',
  perplexity: '#eda100',
  claude: '#e87ba4', anthropic: '#e87ba4',
  grok: '#008300',
};
const FALLBACK_COLORS = ['#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300'];

const platformColor = (name: string, index: number): string =>
  PLATFORM_COLOR_MAP[name.toLowerCase()] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length];

const scoreTone = (score?: number | null) => {
  if (score === null || score === undefined) return { text: 'text-gray-500', bar: 'bg-gray-300' };
  if (score >= 80) return { text: 'text-emerald-700', bar: 'bg-emerald-500' };
  if (score >= 60) return { text: 'text-blue-700', bar: 'bg-blue-500' };
  if (score >= 40) return { text: 'text-amber-700', bar: 'bg-amber-500' };
  return { text: 'text-red-700', bar: 'bg-red-500' };
};

const COMPONENT_LABELS: Record<string, string> = {
  visibility: 'Visibility',
  authority: 'Authority',
  competitive_share: 'Competitive Share',
  conversion: 'Conversion',
  sentiment: 'Sentiment',
};

function stripHtml(html: string): string {
  try {
    return new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
  } catch {
    return html;
  }
}

const formatDay = (ts: number): string =>
  new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const formatFullDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

export default function QuestionPerformanceModal({ question, isOpen, onClose }: QuestionPerformanceModalProps) {
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [scores, setScores] = useState<QuestionScoreEntry[]>([]);
  const [versions, setVersions] = useState<QuestionAnswerVersion[]>([]);
  const [showPlatforms, setShowPlatforms] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    if (!isOpen || !question) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setShowPlatforms(false);
    setHistoryOpen(false);
    Promise.all([
      tenantApi.getQuestionScoreHistory(question.id),
      tenantApi.getQuestionVersions(question.id),
    ])
      .then(([scoreRes, versionRes]) => {
        if (cancelled) return;
        setScores(scoreRes.scores || []);
        setVersions(versionRes.versions || []);
      })
      .catch((err) => {
        console.error('Error loading question performance:', err);
        if (!cancelled) setLoadError('Could not load performance data. Please try again.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, question?.id]);

  const chartData = useMemo(() =>
    scores
      .map((s) => {
        const ts = Date.parse(s.report_generated_at || s.created_at);
        if (Number.isNaN(ts)) return null;
        return {
          ts,
          mean: s.mean_score,
          ...(s.platform_scores || {}),
        } as Record<string, number | null> & { ts: number };
      })
      .filter((row): row is NonNullable<typeof row> => row !== null)
      .sort((a, b) => a.ts - b.ts),
    [scores]
  );

  const platformKeys = useMemo(() => {
    const keys = new Set<string>();
    scores.forEach((s) => Object.keys(s.platform_scores || {}).forEach((k) => keys.add(k)));
    return Array.from(keys).sort();
  }, [scores]);

  // Answer edits (everything after the original answer) become vertical markers.
  const editMarkers = useMemo(() =>
    versions
      .filter((v) => v.source !== 'baseline')
      .map((v) => Date.parse(v.created_at))
      .filter((ts) => !Number.isNaN(ts)),
    [versions]
  );

  const latestComponents = useMemo(() => {
    for (let i = scores.length - 1; i >= 0; i--) {
      const cs = scores[i].component_scores;
      if (cs && Object.keys(cs).length > 0) return cs;
    }
    return null;
  }, [scores]);

  const sortedVersions = useMemo(
    () => [...versions].sort((a, b) => b.version - a.version),
    [versions]
  );

  if (!question) return null;

  const trackingSince = question.baselined_at ? formatFullDate(question.baselined_at) : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Question Performance" size="xl">
      <div className="space-y-5">
        <div>
          <p className="text-sm font-medium text-gray-900">{stripHtml(question.question)}</p>
          {trackingSince && (
            <p className="text-xs text-gray-500 mt-1">Tracking since {trackingSince}</p>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <p className="text-sm text-gray-500">Loading performance data…</p>
          </div>
        ) : loadError ? (
          <div className="flex items-center justify-center h-64 bg-red-50 rounded">
            <p className="text-sm text-red-600">{loadError}</p>
          </div>
        ) : (
          <>
            {/* Score trend */}
            {chartData.length > 0 ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-700">Score over time</h4>
                  {platformKeys.length > 0 && (
                    <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showPlatforms}
                        onChange={(e) => setShowPlatforms(e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      By platform
                    </label>
                  )}
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={chartData} margin={{ top: 12, right: 16, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="ts"
                      type="number"
                      scale="time"
                      domain={['dataMin', 'dataMax']}
                      tickFormatter={formatDay}
                      style={{ fontSize: '11px' }}
                      stroke="#9ca3af"
                    />
                    <YAxis domain={[0, 100]} style={{ fontSize: '11px' }} stroke="#9ca3af" />
                    <Tooltip
                      labelFormatter={(ts) => new Date(Number(ts)).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                      formatter={(value: number, name: string) => [
                        typeof value === 'number' ? value.toFixed(1) : value,
                        name === 'mean' ? 'Score' : name,
                      ]}
                    />
                    {showPlatforms && <Legend wrapperStyle={{ fontSize: '11px' }} />}
                    {editMarkers.map((ts) => (
                      <ReferenceLine
                        key={ts}
                        x={ts}
                        stroke="#9ca3af"
                        strokeDasharray="4 4"
                        label={{ value: 'Answer updated', position: 'top', fontSize: 10, fill: '#6b7280' }}
                      />
                    ))}
                    <Line
                      type="monotone"
                      dataKey="mean"
                      name="Score"
                      stroke={MEAN_COLOR}
                      strokeWidth={2}
                      dot={{ r: 4, fill: MEAN_COLOR }}
                      connectNulls
                    />
                    {showPlatforms && platformKeys.map((key, i) => (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        name={key}
                        stroke={platformColor(key, i)}
                        strokeWidth={2}
                        strokeDasharray="6 3"
                        dot={{ r: 3 }}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex items-center justify-center h-40 bg-gray-50 rounded">
                <p className="text-sm text-gray-500">
                  No performance data yet — scores appear after the next report is generated.
                </p>
              </div>
            )}

            {/* Latest component breakdown */}
            {latestComponents && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Latest breakdown</h4>
                <div className="space-y-2">
                  {Object.entries(COMPONENT_LABELS)
                    .filter(([key]) => latestComponents[key] !== undefined && latestComponents[key] !== null)
                    .map(([key, label]) => {
                      const value = latestComponents[key];
                      const tone = scoreTone(value);
                      return (
                        <div key={key} className="flex items-center gap-3">
                          <span className="w-36 text-xs text-gray-600">{label}</span>
                          <div className="relative flex-1 bg-gray-100 rounded-full h-2.5">
                            <div
                              className={`h-2.5 rounded-full ${tone.bar} transition-all`}
                              style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
                            />
                          </div>
                          <span className={`w-10 text-right text-xs font-medium ${tone.text}`}>
                            {value.toFixed(0)}
                          </span>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Answer history */}
            {sortedVersions.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={() => setHistoryOpen(!historyOpen)}
                  className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-gray-900"
                >
                  {historyOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  Answer history ({sortedVersions.length})
                </button>
                {historyOpen && (
                  <ul className="mt-3 space-y-3">
                    {sortedVersions.map((v) => (
                      <li key={v.id} className="bg-gray-50 rounded p-3">
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                          <span>
                            {v.source === 'baseline' ? 'Original answer' : 'Answer updated'}
                            {v.changed_by ? ` · ${v.changed_by}` : ''}
                          </span>
                          <span>{formatFullDate(v.created_at)}</span>
                        </div>
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{stripHtml(v.answer)}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
